import { RateAmountType } from '@api/index';
import type { TourPackageRoomPriceItemViewModel } from '@api/models/TourPackageRoomPriceItemViewModel';
import { getCurrencySymbol } from '@utils/utils';

export interface CashDiscountFields {
  cashDiscountRateAmountType?: RateAmountType | string;
  cashDiscountRateAmountValue?: number | null;
  cashDiscountCurrencyName?: string | null;
  originalCurrency?: string | null;
}

export const getSinglePersonDisplayPrice = (
  prices?: TourPackageRoomPriceItemViewModel[] | null,
  fallbackPrice?: number | null
): number | undefined => {
  const singlePersonPrice = prices?.find((item) => item.personCount === 1);

  return (
    singlePersonPrice?.discountedPrice ??
    singlePersonPrice?.price ??
    fallbackPrice ??
    undefined
  );
};

const isTryCurrency = (currencyCode?: string | null): boolean => {
  const code = currencyCode?.trim().toLowerCase();
  return !code || code === 'tl' || code === 'try' || code === '₺';
};

export const getPriceStartingFromLabel = (
  currencyCode?: string | null
): string => (isTryCurrency(currencyCode) ? "'den itibaren" : "'dan itibaren");

type TryEquivalentPriceFields = {
  originalCurrency?: string | null;
  tryTotalDiscountedSellingPrice?: number | null;
  tryTotalSellingPrice?: number | null;
};

export const getTryEquivalentPrice = (
  variation?: TryEquivalentPriceFields | null
): number | null => {
  if (!variation || isTryCurrency(variation.originalCurrency)) return null;

  const tryPrice =
    variation.tryTotalDiscountedSellingPrice ?? variation.tryTotalSellingPrice;

  return tryPrice != null && tryPrice > 0 ? tryPrice : null;
};

export const formatTryEquivalentPriceLabel = (price: number): string =>
  `${price.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${getCurrencySymbol('TRY')}`;

export const formatCashDiscountBadgeText = (
  discount: CashDiscountFields,
  fallbackCurrency?: string | null
): string | null => {
  const value = discount.cashDiscountRateAmountValue;
  if (value == null || Number.isNaN(value)) return null;

  const isPercentage =
    discount.cashDiscountRateAmountType === RateAmountType.PERCENTAGE ||
    discount.cashDiscountRateAmountType === 'Percentage';

  if (isPercentage) return `%${value} İndirim`;

  const currency = getCurrencySymbol(
    discount.cashDiscountCurrencyName ||
      discount.originalCurrency ||
      fallbackCurrency
  );
  return `${value} ${currency} İndirim`.trim();
};

export const formatExtraVariationDescription = (
  rateAmountValue?: number,
  rateAmountType?: RateAmountType | string,
  currencyCode?: string | null
): string => {
  if (rateAmountValue == null) return 'Standart Paket Fiyatına Ek İndirim';

  const isPercentage =
    rateAmountType === RateAmountType.PERCENTAGE ||
    rateAmountType === 'Percentage' ||
    rateAmountType == null;

  if (isPercentage) {
    return `Standart Paket Fiyatına Ek %${rateAmountValue} İndirim`;
  }

  return `Standart Paket Fiyatına Ek ${rateAmountValue} ${getCurrencySymbol(
    currencyCode
  )} İndirim`.trim();
};
